import React, { useState, useEffect } from 'react';
import {
  Wallet, Download, CheckCircle, AlertTriangle, Users, DollarSign, XCircle
} from 'lucide-react';
import { downloadCSV } from '../utils/csvExport';
import { supabase } from '../supabase';
import {
  formatCurrency, getMonthLabel, getCurrentPeriodMonth, getLastNMonths
} from '../utils/commissionCalc';
import { logAdminAction } from '../utils/auditLog';

const getAdminEmail = () => {
  try { return JSON.parse(localStorage.getItem('workvan_internal_auth'))?.email; } catch(e) { return null; }
};

const MINIMUM_PAYOUT = 50;

export default function Payouts() {
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentPeriodMonth());
  const [payoutData, setPayoutData] = useState({ eligible: [], ineligible: [] });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [markingPaid, setMarkingPaid] = useState(false);
  const [earlyRequests, setEarlyRequests] = useState([]);
  const [processingRequest, setProcessingRequest] = useState(null);

  const months = getLastNMonths(12);

  useEffect(() => {
    loadPayoutData();
    loadEarlyRequests();
  }, [selectedMonth]); // eslint-disable-line

  const loadPayoutData = async () => {
    setLoading(true);
    setSelectedIds(new Set());

    try {
      // Get all owed commissions for selected month with affiliate info
      const { data: commissions } = await supabase
        .from('affiliate_commissions')
        .select(`
          *,
          affiliates (
            id, name,
            payout_setup_complete, payout_setup_skipped,
            bank_name, account_type, routing_number, account_number
          )
        `)
        .eq('period_month', selectedMonth)
        .eq('status', 'owed');

      // Group by affiliate
      const affiliateMap = {};
      (commissions || []).forEach(c => {
        const affId = c.affiliate_id;
        if (!affiliateMap[affId]) {
          affiliateMap[affId] = {
            affiliate: c.affiliates,
            commissionIds: [],
            totalOwed: 0
          };
        }
        affiliateMap[affId].commissionIds.push(c.id);
        affiliateMap[affId].totalOwed += parseFloat(c.commission_amount) || 0;
      });

      const eligible = [];
      const ineligible = [];

      Object.values(affiliateMap).forEach(entry => {
        const aff = entry.affiliate;
        if (!aff) return;

        const setupIncomplete = !aff.payout_setup_complete || aff.payout_setup_skipped;
        const belowMinimum = entry.totalOwed < MINIMUM_PAYOUT;

        if (setupIncomplete || belowMinimum) {
          const reasons = [];
          if (setupIncomplete) reasons.push('Setup incomplete');
          if (belowMinimum) reasons.push(`Below $${MINIMUM_PAYOUT} minimum`);
          ineligible.push({ ...entry, reasons });
        } else {
          eligible.push(entry);
        }
      });

      // Sort by name
      eligible.sort((a, b) => (a.affiliate?.name || '').localeCompare(b.affiliate?.name || ''));
      ineligible.sort((a, b) => (a.affiliate?.name || '').localeCompare(b.affiliate?.name || ''));

      setPayoutData({ eligible, ineligible });
    } catch (error) {
      console.error('Error loading payout data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEarlyRequests = async () => {
    try {
      const { data } = await supabase
        .from('affiliate_payout_requests')
        .select(`
          *,
          affiliates (id, name, code, bank_name, account_type, routing_number, account_number)
        `)
        .eq('status', 'pending')
        .order('requested_at', { ascending: true });
      setEarlyRequests(data || []);
    } catch {}
  };

  const handleApproveRequest = async (request) => {
    if (!window.confirm(`Approve early payout of ${formatCurrency(request.net_amount)} for ${request.affiliates?.name}?`)) return;
    setProcessingRequest(request.id);
    try {
      await supabase
        .from('affiliate_payout_requests')
        .update({ status: 'approved', processed_at: new Date().toISOString(), processed_by: getAdminEmail() })
        .eq('id', request.id);

      // Mark their owed commissions as paid
      await supabase
        .from('affiliate_commissions')
        .update({ status: 'paid', paid_at: new Date().toISOString() })
        .eq('affiliate_id', request.affiliate_id)
        .eq('status', 'owed');

      await logAdminAction(getAdminEmail(), 'early_payout_approved', 'payout_request', request.id, request.affiliates?.name, { amount: request.amount, fee: request.fee, net: request.net_amount });
      loadEarlyRequests();
      loadPayoutData();
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleDenyRequest = async (request) => {
    if (!window.confirm(`Deny early payout request from ${request.affiliates?.name}?`)) return;
    setProcessingRequest(request.id);
    try {
      await supabase
        .from('affiliate_payout_requests')
        .update({ status: 'denied', processed_at: new Date().toISOString(), processed_by: getAdminEmail() })
        .eq('id', request.id);

      await logAdminAction(getAdminEmail(), 'early_payout_denied', 'payout_request', request.id, request.affiliates?.name, { amount: request.amount });
      loadEarlyRequests();
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setProcessingRequest(null);
    }
  };

  const maskValue = (val) => {
    if (!val) return '-';
    const str = String(val);
    return str.length > 4 ? '****' + str.slice(-4) : str;
  };

  const toggleSelect = (affiliateId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(affiliateId)) {
        next.delete(affiliateId);
      } else {
        next.add(affiliateId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === payoutData.eligible.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(payoutData.eligible.map(e => e.affiliate.id)));
    }
  };

  const handleMarkAsPaid = async () => {
    if (selectedIds.size === 0) return;

    const selectedEntries = payoutData.eligible.filter(e => selectedIds.has(e.affiliate.id));
    const totalAmount = selectedEntries.reduce((sum, e) => sum + e.totalOwed, 0);
    const allCommissionIds = selectedEntries.flatMap(e => e.commissionIds);

    if (!window.confirm(
      `Mark ${selectedEntries.length} affiliate(s) as paid?\n\nTotal: ${formatCurrency(totalAmount)}\nCommission records: ${allCommissionIds.length}`
    )) return;

    setMarkingPaid(true);
    try {
      const now = new Date().toISOString();

      await supabase
        .from('affiliate_commissions')
        .update({ status: 'paid', paid_at: now })
        .in('id', allCommissionIds);

      const adminEmail = getAdminEmail();
      await logAdminAction(
        adminEmail,
        'payout_bulk_paid',
        'commission',
        null,
        `${selectedEntries.length} affiliates paid`,
        {
          affiliate_count: selectedEntries.length,
          commission_count: allCommissionIds.length,
          total_amount: totalAmount,
          period: selectedMonth,
          affiliate_names: selectedEntries.map(e => e.affiliate?.name)
        }
      );

      loadPayoutData();
    } catch (error) {
      alert('Error marking as paid: ' + error.message);
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleGenerateCSV = () => {
    const csvData = payoutData.eligible.map(entry => ({
      affiliate_name: entry.affiliate?.name || 'Unknown',
      bank_name: entry.affiliate?.bank_name || '',
      account_type: entry.affiliate?.account_type || '',
      routing_last4: maskValue(entry.affiliate?.routing_number),
      account_last4: maskValue(entry.affiliate?.account_number),
      total_owed: entry.totalOwed
    }));

    const year = selectedMonth.substring(0, 4);
    const month = selectedMonth.substring(5, 7);

    downloadCSV(csvData, [
      { key: 'affiliate_name', label: 'Affiliate Name' },
      { key: 'bank_name', label: 'Bank Name' },
      { key: 'account_type', label: 'Account Type' },
      { key: 'routing_last4', label: 'Routing (last 4)' },
      { key: 'account_last4', label: 'Account (last 4)' },
      { key: 'total_owed', label: 'Total Owed', format: (v) => v ? Number(v).toFixed(2) : '0.00' }
    ], `payout-report-${year}-${month}.csv`);
  };

  // Summary calculations
  const totalPayable = payoutData.eligible.reduce((sum, e) => sum + e.totalOwed, 0);
  const eligibleCount = payoutData.eligible.length;
  const ineligibleCount = payoutData.ineligible.length;

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
        Loading payout data...
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem 3rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#e0e0e0',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <Wallet size={28} color="#ff6b35" />
            Payouts
          </h1>
          <p style={{ color: '#888' }}>
            Affiliate payout reports and disbursement tracking
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            onClick={handleGenerateCSV}
            disabled={payoutData.eligible.length === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1rem',
              background: '#2a2a2a',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              color: payoutData.eligible.length === 0 ? '#555' : '#e0e0e0',
              fontWeight: '600',
              cursor: payoutData.eligible.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '0.85rem',
              opacity: payoutData.eligible.length === 0 ? 0.5 : 1
            }}
          >
            <Download size={18} />
            Generate CSV
          </button>
          {selectedIds.size > 0 && (
            <button
              onClick={handleMarkAsPaid}
              disabled={markingPaid}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: markingPaid ? '#555' : '#4ecca3',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontWeight: '600',
                cursor: markingPaid ? 'not-allowed' : 'pointer',
                opacity: markingPaid ? 0.7 : 1
              }}
            >
              <CheckCircle size={18} />
              {markingPaid ? 'Processing...' : `Mark as Paid (${selectedIds.size})`}
            </button>
          )}
        </div>
      </div>

      {/* Month Selector */}
      <div style={{ marginBottom: '1.5rem' }}>
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          style={{
            padding: '0.75rem 1rem',
            background: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            color: '#e0e0e0',
            fontWeight: '600',
            minWidth: '180px'
          }}
        >
          {months.map(month => (
            <option key={month} value={month}>{getMonthLabel(month)}</option>
          ))}
        </select>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1.25rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #ff6b35'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <DollarSign size={18} color="#ff6b35" />
            <span style={{ color: '#888', fontSize: '0.85rem' }}>Total Payable</span>
          </div>
          <div style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#ff6b35',
            fontFamily: '"JetBrains Mono", monospace'
          }}>
            {formatCurrency(totalPayable)}
          </div>
        </div>

        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1.25rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #4ecca3'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <Users size={18} color="#4ecca3" />
            <span style={{ color: '#888', fontSize: '0.85rem' }}>Affiliates to Pay</span>
          </div>
          <div style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#4ecca3',
            fontFamily: '"JetBrains Mono", monospace'
          }}>
            {eligibleCount}
          </div>
        </div>

        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1.25rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #f39c12'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <AlertTriangle size={18} color="#f39c12" />
            <span style={{ color: '#888', fontSize: '0.85rem' }}>Ineligible</span>
          </div>
          <div style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#f39c12',
            fontFamily: '"JetBrains Mono", monospace'
          }}>
            {ineligibleCount}
          </div>
        </div>
      </div>

      {/* Early Payout Requests */}
      {earlyRequests.length > 0 && (
        <div style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          border: '1px solid #f39c1240',
          overflow: 'hidden',
          marginBottom: '2rem'
        }}>
          <div style={{
            padding: '1rem 1.25rem',
            borderBottom: '1px solid #2a2a2a',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: '#f39c1210'
          }}>
            <AlertTriangle size={18} color="#f39c12" />
            <span style={{ color: '#f39c12', fontWeight: '600' }}>
              Early Payout Requests
            </span>
            <span style={{ color: '#888', fontSize: '0.85rem' }}>
              ({earlyRequests.length} pending)
            </span>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem', color: '#888', fontWeight: '500' }}>Affiliate</th>
                <th style={{ textAlign: 'right', padding: '0.75rem 1rem', color: '#888', fontWeight: '500' }}>Amount</th>
                <th style={{ textAlign: 'right', padding: '0.75rem 1rem', color: '#888', fontWeight: '500' }}>Fee</th>
                <th style={{ textAlign: 'right', padding: '0.75rem 1rem', color: '#888', fontWeight: '500' }}>Net</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem', color: '#888', fontWeight: '500' }}>Bank</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem', color: '#888', fontWeight: '500' }}>Requested</th>
                <th style={{ textAlign: 'center', padding: '0.75rem 1rem', color: '#888', fontWeight: '500' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {earlyRequests.map(req => {
                const aff = req.affiliates;
                const isProcessing = processingRequest === req.id;
                return (
                  <tr key={req.id} style={{ borderBottom: '1px solid #2a2a2a' }}>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <div style={{ fontWeight: '600', color: '#e0e0e0' }}>{aff?.name || 'Unknown'}</div>
                      <div style={{ fontSize: '0.75rem', color: '#666' }}>{aff?.code}</div>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#e0e0e0', fontFamily: '"JetBrains Mono", monospace' }}>
                      {formatCurrency(req.amount)}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#e74c3c', fontFamily: '"JetBrains Mono", monospace' }}>
                      -{formatCurrency(req.fee)}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#4ecca3', fontWeight: '700', fontFamily: '"JetBrains Mono", monospace' }}>
                      {formatCurrency(req.net_amount)}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: '#888', fontSize: '0.85rem' }}>
                      {aff?.bank_name || 'Not set up'}
                      {aff?.account_type && <span> ({aff.account_type})</span>}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: '#888', fontSize: '0.85rem' }}>
                      {new Date(req.requested_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleApproveRequest(req)}
                          disabled={isProcessing}
                          style={{
                            padding: '0.35rem 0.75rem',
                            background: '#4ecca320',
                            border: 'none',
                            borderRadius: '4px',
                            color: '#4ecca3',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: isProcessing ? 'wait' : 'pointer',
                            opacity: isProcessing ? 0.5 : 1
                          }}
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleDenyRequest(req)}
                          disabled={isProcessing}
                          style={{
                            padding: '0.35rem 0.75rem',
                            background: '#e74c3c20',
                            border: 'none',
                            borderRadius: '4px',
                            color: '#e74c3c',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: isProcessing ? 'wait' : 'pointer',
                            opacity: isProcessing ? 0.5 : 1
                          }}
                        >
                          Deny
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Eligible Payouts Table */}
      <div style={{
        background: '#1a1a1a',
        borderRadius: '12px',
        border: '1px solid #2a2a2a',
        overflow: 'hidden',
        marginBottom: '2rem'
      }}>
        <div style={{
          padding: '1rem 1.25rem',
          borderBottom: '1px solid #2a2a2a',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <CheckCircle size={18} color="#4ecca3" />
          <span style={{ color: '#e0e0e0', fontWeight: '600' }}>
            Eligible Payouts
          </span>
          <span style={{ color: '#888', fontSize: '0.85rem' }}>
            ({eligibleCount})
          </span>
        </div>

        {payoutData.eligible.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
            <Wallet size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
            <p>No eligible payouts for {getMonthLabel(selectedMonth)}.</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                <th style={{ textAlign: 'center', padding: '1rem 0.75rem', width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.size === payoutData.eligible.length && payoutData.eligible.length > 0}
                    onChange={toggleSelectAll}
                    style={{ cursor: 'pointer', accentColor: '#ff6b35' }}
                  />
                </th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Affiliate Name
                </th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Bank Name
                </th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Account Type
                </th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Routing (last 4)
                </th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Account (last 4)
                </th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Total Owed
                </th>
              </tr>
            </thead>
            <tbody>
              {payoutData.eligible.map(entry => {
                const aff = entry.affiliate;
                const isSelected = selectedIds.has(aff.id);
                return (
                  <tr
                    key={aff.id}
                    style={{
                      borderBottom: '1px solid #2a2a2a',
                      background: isSelected ? '#ff6b3510' : 'transparent'
                    }}
                  >
                    <td style={{ textAlign: 'center', padding: '1rem 0.75rem' }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(aff.id)}
                        style={{ cursor: 'pointer', accentColor: '#ff6b35' }}
                      />
                    </td>
                    <td style={{ padding: '1rem', fontWeight: '600', color: '#e0e0e0' }}>
                      {aff.name || 'Unknown'}
                    </td>
                    <td style={{ padding: '1rem', color: '#e0e0e0' }}>
                      {aff.bank_name || '-'}
                    </td>
                    <td style={{ padding: '1rem', color: '#e0e0e0', textTransform: 'capitalize' }}>
                      {aff.account_type || '-'}
                    </td>
                    <td style={{ padding: '1rem', color: '#888', fontFamily: '"JetBrains Mono", monospace' }}>
                      {maskValue(aff.routing_number)}
                    </td>
                    <td style={{ padding: '1rem', color: '#888', fontFamily: '"JetBrains Mono", monospace' }}>
                      {maskValue(aff.account_number)}
                    </td>
                    <td style={{
                      padding: '1rem',
                      textAlign: 'right',
                      fontWeight: '700',
                      color: '#4ecca3',
                      fontFamily: '"JetBrains Mono", monospace'
                    }}>
                      {formatCurrency(entry.totalOwed)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Ineligible Section */}
      {payoutData.ineligible.length > 0 && (
        <div style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          border: '1px solid #2a2a2a',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '1rem 1.25rem',
            borderBottom: '1px solid #2a2a2a',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <XCircle size={18} color="#f39c12" />
            <span style={{ color: '#e0e0e0', fontWeight: '600' }}>
              Ineligible
            </span>
            <span style={{ color: '#888', fontSize: '0.85rem' }}>
              ({ineligibleCount})
            </span>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Affiliate Name
                </th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Total Owed
                </th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Reason
                </th>
              </tr>
            </thead>
            <tbody>
              {payoutData.ineligible.map(entry => {
                const aff = entry.affiliate;
                return (
                  <tr key={aff.id} style={{ borderBottom: '1px solid #2a2a2a' }}>
                    <td style={{ padding: '1rem', fontWeight: '600', color: '#e0e0e0' }}>
                      {aff.name || 'Unknown'}
                    </td>
                    <td style={{
                      padding: '1rem',
                      textAlign: 'right',
                      fontWeight: '700',
                      color: '#888',
                      fontFamily: '"JetBrains Mono", monospace'
                    }}>
                      {formatCurrency(entry.totalOwed)}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {entry.reasons.map((reason, idx) => (
                          <span
                            key={idx}
                            style={{
                              padding: '0.25rem 0.75rem',
                              background: reason.includes('Setup') ? '#e74c3c20' : '#f39c1220',
                              color: reason.includes('Setup') ? '#e74c3c' : '#f39c12',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}
                          >
                            {reason}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
